import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────────────────
// Video engine — SINGLE SOURCE OF TRUTH.
// Preset catalog, size→bitrate math, trim math, zod schemas, and the isomorphic
// engine contract live here so the client (pre-compression / download) and the
// server (compress-on-arrival / size-targeted download) compute identical targets.
// No runtime deps beyond zod — safe to import from both browser and Node.
// ─────────────────────────────────────────────────────────────────────────────
const uuid = z.string().uuid();
const ts = z.string();
// Decimal units — matches how casting platforms phrase limits ("1 GB", "150 MB").
export const KB = 1_000;
export const MB = 1_000_000;
export const GB = 1_000_000_000;
export const VIDEO_PRESETS = [
    { id: 'standard_1080', label: 'Standard — 1080p · 500 MB', targetBytes: 500 * MB, maxLongEdge: 1920, audioBitrate: 128_000, note: 'A safe, sharp default for most casting submissions.' },
    { id: 'tape_720', label: 'Self-tape — 720p · 200 MB', targetBytes: 200 * MB, maxLongEdge: 1280, audioBitrate: 128_000, note: 'Smaller, fast to upload; great for self-tapes.' },
    { id: 'cf_150', label: 'Casting Frontier — 150 MB', targetBytes: 150 * MB, maxLongEdge: 1280, audioBitrate: 128_000, note: 'Fits tighter per-file caps.' },
    { id: 'cn_600', label: 'Casting Networks — 600 MB', targetBytes: 600 * MB, maxLongEdge: 1920, audioBitrate: 128_000 },
    { id: 'aa_1000', label: 'Actors Access — 1 GB', targetBytes: 1 * GB, maxLongEdge: 1920, audioBitrate: 160_000 },
    { id: 'email_25', label: 'Email-safe — 25 MB', targetBytes: 25 * MB, maxLongEdge: 960, audioBitrate: 96_000, note: 'Prefer a share link for clips over ~1 minute.' },
    { id: 'original_2g', label: 'High quality — up to 2 GB', targetBytes: 2 * GB, maxLongEdge: 1920, audioBitrate: 192_000 },
    { id: 'link_only', label: 'Original — no size limit', targetBytes: null, maxLongEdge: 3840, audioBitrate: 192_000, note: 'Uploads as-is. Best when you will share a link.' },
];
export const DEFAULT_PRESET_ID = 'standard_1080';
export const DEFAULT_TARGET_BYTES = 500 * MB;
/** Hard per-file upload ceiling — KEEP IN SYNC with the Supabase project `fileSizeLimit` (500 MB).
 *  A file over this is rejected by Supabase storage; the uploader offers a forced local compress. */
export const MAX_UPLOAD_BYTES = 500 * MB;
/** When forced to compress an over-limit file, aim safely under the ceiling so the output fits. */
export const UPLOAD_COMPRESS_FALLBACK_BYTES = Math.floor(MAX_UPLOAD_BYTES * 0.92);
export const MIN_TARGET_BYTES = 5 * MB;
export const MAX_TARGET_BYTES = 2 * GB;
export const presetById = (id) => VIDEO_PRESETS.find((p) => p.id === id) ?? null;
/** The smallest preset whose cap is >= n bytes (nearest "fits within" preset). */
export const presetForTargetBytes = (n) => {
    if (n == null)
        return presetById('link_only');
    const sized = VIDEO_PRESETS.filter((p) => p.targetBytes != null);
    const fits = sized.filter((p) => p.targetBytes >= n).sort((a, b) => a.targetBytes - b.targetBytes);
    return fits[0] ?? sized.sort((a, b) => b.targetBytes - a.targetBytes)[0] ?? null;
};
export const clampTargetBytes = (n) => Math.min(MAX_TARGET_BYTES, Math.max(MIN_TARGET_BYTES, Math.round(n)));
// ── Size → bitrate math ──────────────────────────────────────────────────────
// Reserve a safety margin so muxing overhead + VBV don't blow past the cap.
export const SIZE_SAFETY = 0.94;
/** Minimum video bitrate we'll ever request (keeps audio-only edge cases sane). */
export const MIN_VIDEO_BITRATE = 60_000;
/**
 * Maximum video bitrate we will EVER request, whatever the size budget says.
 *
 * ⚠️ This ceiling is not a nicety, its absence was a production bug. `targetBytes` is a CEILING,
 * but the formula below treated it as a QUOTA TO SPEND, so bitrate scaled inversely with duration:
 * a 500 MB budget over a 13.7-second self-tape asked for **275 Mbps**, and over 8 seconds, 459 Mbps.
 *
 * That broke uploads in two ways at once:
 *   1. It INFLATED files. A short clip was re-encoded far larger than the source, so the
 *      "compression" step made the upload slower on the phone connection it was filmed on.
 *   2. It SILENTLY DESTROYED them. H.264 level is chosen from the requested bitrate, so past
 *      ~240 Mbps the codec string escalates to High@L6.1, which browser VideoEncoders refuse.
 *      mediabunny then discards the video track WITHOUT THROWING and emits an audio-only file.
 *
 * 40 Mbps is generous for any self-tape (well above visually lossless at 1080p) and keeps the
 * codec string at a level every encoder supports.
 */
export const MAX_VIDEO_BITRATE = 40_000_000;
/** Default encode tuning — one home for client (mediabunny/wasm) + server (ffmpeg). */
export const DEFAULT_AUDIO_BITRATE = 128_000;
export const DEFAULT_MAX_LONG_EDGE = 1920;
/**
 * Video bitrate (bps) to fit `targetBytes` over `durationSec`, net of audio.
 *
 * `targetBytes` is a CEILING, not a quota: coming in under it is a good outcome, so the result is
 * clamped by both a hard ceiling and (when known) the SOURCE bitrate. Re-encoding above the source
 * cannot add information, it only makes the file bigger and the upload slower.
 *
 * @param sourceBps the probed bitrate of the input, when available. Pass it: it is what stops a
 *   short clip being inflated to fill the budget.
 */
export function calcVideoBitrate(targetBytes, durationSec, audioBps = 128_000, sourceBps) {
    const budget = (targetBytes * 8 * SIZE_SAFETY) / Math.max(durationSec, 0.1) - audioBps;
    const source = sourceBps && sourceBps > 0 ? sourceBps : Number.POSITIVE_INFINITY;
    const capped = Math.min(budget, MAX_VIDEO_BITRATE, source);
    return Math.max(MIN_VIDEO_BITRATE, Math.floor(capped));
}
export const maxrateFor = (videoBps) => Math.round(videoBps * 1.45);
export const bufsizeFor = (videoBps) => Math.round(videoBps * 2);
/**
 * Choose the longest output edge so the per-pixel bit budget stays sharp.
 * Downscales when the bitrate is too low to keep the source resolution crisp;
 * never upscales (capped by source and by the preset cap).
 */
export function resolveLongEdge(videoBps, srcLongEdge, cap) {
    // Resolution tiers keyed by available video bitrate (H.264, ~24–30fps).
    const tiers = [
        [5_000_000, 1920],
        [2_400_000, 1280],
        [1_200_000, 960],
        [600_000, 854],
        [300_000, 640],
        [0, 426],
    ];
    const tier = tiers.find(([min]) => videoBps >= min)?.[1] ?? 426;
    return Math.max(240, Math.min(tier, cap, srcLongEdge || cap));
}
// ── Trim math (non-destructive: in/out stored as ms, applied on download) ─────
export const trimmedDurationSec = (durationMs, inMs = 0, outMs) => Math.max(0.1, ((outMs ?? durationMs) - (inMs || 0)) / 1000);
/** ffmpeg-style timecode "HH:MM:SS.mmm" from milliseconds. */
export function msToTimecode(ms) {
    const totalMs = Math.max(0, Math.round(ms));
    const h = Math.floor(totalMs / 3_600_000);
    const m = Math.floor((totalMs % 3_600_000) / 60_000);
    const s = Math.floor((totalMs % 60_000) / 1_000);
    const msPart = totalMs % 1_000;
    const pad = (n, w = 2) => String(n).padStart(w, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(msPart, 3)}`;
}
/** Human-friendly byte size for UI ("500 MB", "1.5 GB", "25 MB"). */
export function humanBytes(n) {
    if (n == null)
        return '—';
    if (n >= GB)
        return `${(n / GB).toFixed(n % GB === 0 ? 0 : 1)} GB`;
    if (n >= MB)
        return `${Math.round(n / MB)} MB`;
    if (n >= KB)
        return `${Math.round(n / KB)} KB`;
    return `${n} B`;
}
/** Parse a megabyte figure (UI numeric field) into clamped bytes, or null if blank. */
export function mbToBytes(mb) {
    if (mb == null || mb === '')
        return null;
    const n = typeof mb === 'string' ? Number(mb) : mb;
    if (!Number.isFinite(n) || n <= 0)
        return null;
    return clampTargetBytes(Math.round(n * MB));
}
export const bytesToMb = (n) => n == null ? null : Math.round(n / MB);
// ── Zod schemas ──────────────────────────────────────────────────────────────
export const TARGET_DOWNLOAD = z.number().int().min(MIN_TARGET_BYTES).max(MAX_TARGET_BYTES);
export const VIDEO_VISIBILITIES = ['team', 'client'];
export const PROCESSING_STATUSES = ['pending', 'processing', 'ready', 'failed'];
/**
 * Where a video was compressed: on the user's device (WebCodecs or ffmpeg.wasm), on the Render
 * server (ffmpeg), or 'full' = uploaded at full size with NO compression. Tracked per-row to
 * forecast server CPU/load in the admin console.
 */
export const COMPRESS_METHODS = ['webcodecs', 'wasm', 'server', 'full'];
export const isCompressMethod = (v) => typeof v === 'string' && COMPRESS_METHODS.includes(v);
export const videoTrimSchema = z
    .object({
    trim_in_ms: z.number().int().min(0).default(0),
    trim_out_ms: z.number().int().positive().nullable().optional(),
})
    .refine((t) => t.trim_out_ms == null || t.trim_out_ms > t.trim_in_ms, {
    message: 'trim_out_ms must be greater than trim_in_ms',
});
/** The video-aware extension merged into mediaSchema (entities.ts). */
export const videoMetaSchema = z.object({
    duration_ms: z.number().int().nullable().optional(),
    width: z.number().int().nullable().optional(),
    height: z.number().int().nullable().optional(),
    video_codec: z.string().nullable().optional(),
    trim_in_ms: z.number().int().optional(),
    trim_out_ms: z.number().int().nullable().optional(),
    processing_status: z.enum(PROCESSING_STATUSES).nullable().optional(),
    processing_error: z.string().nullable().optional(),
    /** Where compression happened (device vs Render) + the failure-log object path. */
    compress_method: z.enum(COMPRESS_METHODS).nullable().optional(),
    processing_log_path: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    tags: z.array(z.string()).optional(),
    visibility: z.enum(VIDEO_VISIBILITIES).optional(),
    /** Set once the video has been moved out to the agency's Google Drive (RepTree object removed). */
    drive_file_id: z.string().nullable().optional(),
    drive_link: z.string().nullable().optional(),
    updated_at: ts.nullable().optional(),
});
export const mediaAttachmentSchema = z.object({
    id: uuid,
    business_id: uuid,
    media_id: uuid,
    opportunity_id: uuid,
    sort_order: z.number().int().optional(),
    is_current: z.boolean(),
    created_by: uuid.nullable().optional(),
    created_at: ts,
});
/** PATCH body for editing a video's hub metadata + non-destructive trim. */
export const videoPatchSchema = z
    .object({
    title: z.string().max(200).nullable().optional(),
    category: z.string().max(80).nullable().optional(),
    tags: z.array(z.string().max(40)).max(40).optional(),
    visibility: z.enum(VIDEO_VISIBILITIES).optional(),
    trim_in_ms: z.number().int().min(0).optional(),
    trim_out_ms: z.number().int().positive().nullable().optional(),
})
    .refine((b) => b.trim_in_ms == null || b.trim_out_ms == null || b.trim_out_ms > b.trim_in_ms, {
    message: 'trim_out_ms must be greater than trim_in_ms',
});
export const attachInputSchema = z.object({
    opportunity_ids: z.array(uuid).min(1).max(100),
});
/**
 * Resolve the effective download target for a video in priority order:
 * explicit bytes → preset → audition target → default.
 */
export function resolveTargetBytes(opts) {
    if (opts.explicitBytes != null)
        return clampTargetBytes(opts.explicitBytes);
    if (opts.presetId) {
        const p = presetById(opts.presetId);
        if (p)
            return p.targetBytes; // may be null (link_only)
    }
    if (opts.auditionBytes != null)
        return clampTargetBytes(opts.auditionBytes);
    return DEFAULT_TARGET_BYTES;
}
//# sourceMappingURL=video.js.map